import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Search,
  Download,
  Trash2,
  X,
  Loader2,
  RefreshCcw,
  ExternalLink,
  PhoneCall,
  CheckCircle2,
  ShieldAlert,
} from "lucide-react";

import { adminAuthHeader } from "../../lib/adminAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeaders = () => adminAuthHeader();

const STATUS_OPTIONS = [
  { value: "new", label: "New", className: "bg-amber-100 text-amber-800" },
  { value: "contacted", label: "Contacted", className: "bg-sky-100 text-sky-800" },
  { value: "screened", label: "Screened", className: "bg-violet-100 text-violet-800" },
  { value: "enrolled", label: "Enrolled", className: "bg-emerald-100 text-emerald-800" },
  { value: "declined", label: "Declined", className: "bg-rose-100 text-rose-800" },
];
const STATUS_LOOKUP = Object.fromEntries(
  STATUS_OPTIONS.map((s) => [s.value, s]),
);

function formatRelative(iso) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const diffMs = Date.now() - then;
  const min = Math.round(diffMs / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const days = Math.round(hr / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.round(days / 30);
  return `${months}mo ago`;
}

export default function StudyInterestsTab() {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState({ total: 0, items: [] });
  const [filters, setFilters] = useState({
    search: "",
    audience: "",
    status: "",
  });
  const [selected, setSelected] = useState(null);
  const [page, setPage] = useState(0);
  const limit = 25;

  const fetchInterests = async (overrideFilters, overridePage) => {
    setLoading(true);
    try {
      const f = overrideFilters ?? filters;
      const p = overridePage ?? page;
      const params = { limit, offset: p * limit };
      if (f.search) params.search = f.search;
      if (f.audience) params.audience = f.audience;
      if (f.status) params.status_filter = f.status;
      const r = await axios.get(`${API}/admin/study-interests`, {
        headers: getAuthHeaders(),
        params,
      });
      setData(r.data);
    } catch {
      toast.error("Failed to fetch study interests.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInterests({ search: "", audience: "", status: "" }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalPages = Math.max(1, Math.ceil(data.total / limit));

  const applyFilters = () => {
    setPage(0);
    fetchInterests(filters, 0);
  };
  const resetFilters = () => {
    const empty = { search: "", audience: "", status: "" };
    setFilters(empty);
    setPage(0);
    fetchInterests(empty, 0);
  };
  const goToPage = (p) => {
    setPage(p);
    fetchInterests(filters, p);
  };

  const exportCsv = async () => {
    setBusy(true);
    try {
      const params = {};
      if (filters.search) params.search = filters.search;
      if (filters.audience) params.audience = filters.audience;
      if (filters.status) params.status_filter = filters.status;
      const r = await axios.get(`${API}/admin/study-interests/export`, {
        headers: getAuthHeaders(),
        responseType: "blob",
        params,
      });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `meditalk-study-interests-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export ready.");
    } catch {
      toast.error("Export failed.");
    } finally {
      setBusy(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      const r = await axios.put(
        `${API}/admin/study-interests/${id}`,
        { status },
        { headers: getAuthHeaders() },
      );
      setSelected(r.data);
      fetchInterests(filters, page);
      toast.success("Status updated.");
    } catch {
      toast.error("Failed to update status.");
    }
  };

  const updateNotes = async (id, notes) => {
    try {
      const r = await axios.put(
        `${API}/admin/study-interests/${id}`,
        { notes },
        { headers: getAuthHeaders() },
      );
      setSelected(r.data);
      fetchInterests(filters, page);
      toast.success("Notes saved.");
    } catch {
      toast.error("Failed to save notes.");
    }
  };

  const markContacted = async (id) => {
    try {
      const r = await axios.post(
        `${API}/admin/study-interests/${id}/mark-contacted`,
        {},
        { headers: getAuthHeaders() },
      );
      setSelected(r.data);
      fetchInterests(filters, page);
      toast.success("Marked as contacted.");
    } catch {
      toast.error("Couldn't mark as contacted.");
    }
  };

  const deleteInterest = async (id) => {
    if (!window.confirm("Delete this expression of interest? This cannot be undone."))
      return;
    try {
      await axios.delete(`${API}/admin/study-interests/${id}`, {
        headers: getAuthHeaders(),
      });
      toast.success("Deleted.");
      setSelected(null);
      fetchInterests(filters, page);
    } catch {
      toast.error("Failed to delete.");
    }
  };

  const markScammer = async (id, reason) => {
    try {
      const r = await axios.post(
        `${API}/admin/study-interests/${id}/mark-scammer`,
        { reason },
        { headers: getAuthHeaders() },
      );
      setSelected(r.data);
      fetchInterests(filters, page);
      toast.success("Marked as scammer — permanently excluded from the database.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to mark as scammer.");
    }
  };

  const unmarkScammer = async (id) => {
    try {
      const r = await axios.post(
        `${API}/admin/study-interests/${id}/unmark-scammer`,
        {},
        { headers: getAuthHeaders() },
      );
      setSelected(r.data);
      fetchInterests(filters, page);
      toast.success("Scammer flag removed.");
    } catch {
      toast.error("Failed to remove scammer flag.");
    }
  };

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-['Outfit'] font-bold text-[var(--charcoal)]">
            Study interests
          </h1>
          <p className="text-sm text-[var(--olive)] mt-1">
            {data.total.toLocaleString()} total · showing {data.items.length}
          </p>
        </div>
        <button
          onClick={exportCsv}
          disabled={busy}
          className="btn-secondary flex items-center gap-2 disabled:opacity-50"
          data-testid="export-interests-csv"
        >
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
          Export CSV
        </button>
      </div>

      {/* Filters */}
      <div className="card p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="relative lg:col-span-2">
            <Search size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--olive)]" />
            <input
              data-testid="interests-search"
              className="w-full h-10 pl-9 pr-4 rounded-lg border border-[var(--sage)]/20 text-[var(--charcoal)]"
              placeholder="Search name, email, study, location"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              onKeyDown={(e) => e.key === "Enter" && applyFilters()}
            />
          </div>
          <select
            data-testid="interests-audience"
            className="h-10 px-3 rounded-lg border border-[var(--sage)]/20 text-[var(--charcoal)] bg-white"
            value={filters.audience}
            onChange={(e) => setFilters({ ...filters, audience: e.target.value })}
          >
            <option value="">All audiences</option>
            <option value="patient">Patients</option>
            <option value="caregiver">Caregivers</option>
          </select>
          <select
            data-testid="interests-status"
            className="h-10 px-3 rounded-lg border border-[var(--sage)]/20 text-[var(--charcoal)] bg-white"
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          >
            <option value="">All statuses</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>
        <div className="flex gap-2 mt-3">
          <button onClick={applyFilters} className="btn-primary" data-testid="apply-filters">
            Apply
          </button>
          <button onClick={resetFilters} className="btn-secondary flex items-center gap-2" data-testid="reset-filters">
            <RefreshCcw size={14} />
            Reset
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="admin-table" data-testid="interests-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email / Phone</th>
                <th>Study</th>
                <th>When to call</th>
                <th>IP</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="text-center py-10 text-[var(--olive)]">
                  <Loader2 className="animate-spin inline-block mr-2" size={18} />
                  Loading…
                </td></tr>
              ) : data.items.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-10 text-[var(--olive)]">
                  No expressions of interest yet.
                </td></tr>
              ) : (
                data.items.map((r) => {
                  const status = STATUS_LOOKUP[r.status] || STATUS_LOOKUP.new;
                  // Count how many other rows in the loaded set share
                  // this submitter_hash , used to surface a "same
                  // source" badge so the admin can spot multi-
                  // submissions from one device quickly.
                  const sameSource = r.submitter_hash
                    ? data.items.filter(
                        (o) => o.id !== r.id && o.submitter_hash === r.submitter_hash,
                      ).length
                    : 0;
                  return (
                    <tr
                      key={r.id}
                      data-testid={`interest-row-${r.id}`}
                      className={r.is_scammer ? "bg-red-50/70" : ""}
                    >
                      <td>
                        <div className="font-medium text-[var(--charcoal)]">
                          {r.first_name || ""} {r.last_name || ""}
                        </div>
                        <div className="text-xs text-[var(--olive)]">
                          {r.audience} · {r.location}
                        </div>
                        {r.is_scammer && (
                          <span
                            className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-red-600 text-white cursor-help"
                            title={r.scammer_reason || "Marked as scammer"}
                            data-testid={`interest-scammer-badge-${r.id}`}
                          >
                            <ShieldAlert size={10} /> Scammer
                          </span>
                        )}
                      </td>
                      <td className="text-sm text-[var(--olive)]">
                        <div className="truncate max-w-[220px]">{r.email}</div>
                        <div className="text-xs">{r.phone_prefix} {r.phone_number}</div>
                      </td>
                      <td className="text-sm text-[var(--charcoal)] max-w-[240px]">
                        <div className="truncate">{r.study_title}</div>
                        {r.source === "screener" && (
                          <span className="inline-flex items-center gap-1 text-xs text-violet-700 mt-1">
                            <ExternalLink size={11} /> via screener
                          </span>
                        )}
                      </td>
                      <td className="text-sm text-[var(--charcoal)]">
                        {formatDateTime(r.preferred_call_at)}
                      </td>
                      <td className="text-xs text-[var(--olive)]">
                        {r.ip_address ? (
                          <span
                            className="font-mono"
                            title={`Source hash: ${r.submitter_hash?.slice(0, 12) || "—"}…`}
                            data-testid={`interest-ip-${r.id}`}
                          >
                            {r.ip_address}
                          </span>
                        ) : r.submitter_hash ? (
                          <span
                            className="text-[var(--olive)] italic"
                            title="Raw IP purged after 90 days; same-source detection still active"
                            data-testid={`interest-ip-${r.id}`}
                          >
                            redacted
                          </span>
                        ) : (
                          "—"
                        )}
                        {sameSource > 0 && (
                          <div
                            className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold uppercase tracking-wider"
                            title="Another row in this list shares the same source hash"
                            data-testid={`interest-same-source-${r.id}`}
                          >
                            ⚠ Same source ×{sameSource + 1}
                          </div>
                        )}
                      </td>
                      <td>
                        <span
                          className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${status.className}`}
                        >
                          {status.label}
                        </span>
                        {r.last_contacted_at ? (
                          <div
                            className="text-[11px] text-[var(--olive)] mt-1 flex items-center gap-1"
                            data-testid={`interest-last-contacted-${r.id}`}
                          >
                            <CheckCircle2 size={11} className="text-emerald-600" />
                            Contacted {formatRelative(r.last_contacted_at)}
                          </div>
                        ) : r.status === "new" &&
                          new Date(r.created_at).getTime() <
                            Date.now() - 24 * 3600 * 1000 ? (
                          <div
                            className="text-[11px] text-rose-600 mt-1 font-medium"
                            data-testid={`interest-stale-${r.id}`}
                          >
                            Waiting {formatRelative(r.created_at)}
                          </div>
                        ) : null}
                      </td>
                      <td>
                        <button
                          onClick={() => setSelected(r)}
                          className="text-sm font-semibold text-[var(--teal-dark)] hover:underline"
                          data-testid={`open-interest-${r.id}`}
                        >
                          Open
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {data.total > limit && (
          <div className="flex items-center justify-between p-4 border-t border-[var(--sage)]/10">
            <span className="text-sm text-[var(--olive)]">
              Page {page + 1} of {totalPages}
            </span>
            <div className="flex gap-2">
              <button disabled={page === 0} onClick={() => goToPage(page - 1)}
                className="btn-secondary disabled:opacity-50 px-4 py-2 text-sm">
                Previous
              </button>
              <button disabled={page + 1 >= totalPages} onClick={() => goToPage(page + 1)}
                className="btn-secondary disabled:opacity-50 px-4 py-2 text-sm">
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {selected && (
        <InterestDetail
          interest={selected}
          onClose={() => setSelected(null)}
          onUpdateStatus={updateStatus}
          onUpdateNotes={updateNotes}
          onDelete={deleteInterest}
          onMarkContacted={markContacted}
          onMarkScammer={markScammer}
          onUnmarkScammer={unmarkScammer}
        />
      )}
    </>
  );
}

function formatDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function InterestDetail({
  interest,
  onClose,
  onUpdateStatus,
  onUpdateNotes,
  onDelete,
  onMarkContacted,
  onMarkScammer,
  onUnmarkScammer,
}) {
  const [notes, setNotes] = useState(interest.notes || "");
  const [notesError, setNotesError] = useState(false);
  const dirty = notes !== (interest.notes || "");

  const handleMarkScammer = () => {
    if (notes.trim().length < 5) {
      setNotesError(true);
      toast.error(
        "You must explain why in the Admin notes field before marking as a scammer.",
      );
      return;
    }
    if (
      !window.confirm(
        `Mark ${interest.first_name || ""} ${interest.last_name || ""} as a scammer? ` +
          "Their email will be PERMANENTLY blocked — future submissions are discarded " +
          "and they can never be included in the database or any export.",
      )
    )
      return;
    onMarkScammer(interest.id, notes.trim());
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div
        className="admin-modal-panel bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="interest-detail-modal"
      >
        <div className="p-6 border-b border-[var(--sage)]/10 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-['Outfit'] font-semibold text-[var(--charcoal)]">
              {interest.first_name} {interest.last_name}
            </h2>
            <p className="text-sm text-[var(--olive)]">
              {interest.audience} · expressed interest in{" "}
              <span className="font-medium text-[var(--charcoal)]">
                {interest.study_title}
              </span>
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[var(--sage)]/10 rounded-lg">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {interest.is_scammer && (
            <div
              className="rounded-xl border border-red-200 bg-red-50 p-4 flex items-start gap-3"
              data-testid="scammer-banner"
            >
              <ShieldAlert size={18} className="text-red-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-bold text-red-700">
                  Marked as scammer — permanently excluded
                </p>
                <p className="text-xs text-red-600 mt-0.5">
                  {interest.scammer_marked_by ? `By ${interest.scammer_marked_by}` : ""}
                  {interest.scammer_marked_at
                    ? ` · ${new Date(interest.scammer_marked_at).toLocaleString()}`
                    : ""}
                </p>
                {interest.scammer_reason && (
                  <p className="text-xs text-red-700 mt-1 whitespace-pre-line">
                    “{interest.scammer_reason}”
                  </p>
                )}
                <p className="text-[11px] text-red-500 mt-1.5">
                  Future submissions from {interest.email} are silently discarded and
                  they are excluded from every export — even if this record is deleted.
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  if (
                    window.confirm(
                      "Remove the scammer flag? Their email will be taken off the permanent blocklist.",
                    )
                  )
                    onUnmarkScammer(interest.id);
                }}
                className="text-xs font-semibold text-red-700 border border-red-300 rounded-full px-3 py-1.5 hover:bg-red-100 flex-shrink-0"
                data-testid="unmark-scammer-btn"
              >
                Unmark
              </button>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <Info label="Email">
              <a href={`mailto:${interest.email}`} className="text-[var(--teal-dark)] hover:underline">
                {interest.email}
              </a>
            </Info>
            <Info label="Phone">
              <a href={`tel:${interest.phone_prefix}${interest.phone_number}`} className="text-[var(--teal-dark)] hover:underline">
                {interest.phone_prefix} {interest.phone_number}
              </a>
            </Info>
            <Info label="Location">{interest.location}</Info>
            <Info label="Best time to call">
              {formatDateTime(interest.preferred_call_at)}
            </Info>
            <Info label="Source">
              {interest.source === "screener" ? "After screener" : "Direct"}
            </Info>
            <Info label="Submitted">
              {formatDateTime(interest.created_at)}
            </Info>
            <Info label="IP address">
              {interest.ip_address ? (
                <span className="font-mono text-sm">{interest.ip_address}</span>
              ) : interest.submitter_hash ? (
                <span className="italic text-[var(--olive)] text-sm">
                  Redacted (older than 90 days)
                </span>
              ) : (
                <span className="text-[var(--olive)]">—</span>
              )}
            </Info>
            <Info label="Source hash">
              {interest.submitter_hash ? (
                <span
                  className="font-mono text-xs break-all text-[var(--olive)]"
                  title="SHA-256(ip + salt) , permanent same-source identifier"
                >
                  {interest.submitter_hash}
                </span>
              ) : (
                <span className="text-[var(--olive)]">—</span>
              )}
            </Info>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-[var(--charcoal)] mb-2">
              Follow-up
            </h3>
            <div
              className="rounded-xl border border-[var(--light-border)] bg-[var(--bone-soft)] p-4 mb-3"
              data-testid="interest-followup-card"
            >
              {interest.last_contacted_at ? (
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div className="flex items-start gap-2.5">
                    <CheckCircle2
                      size={18}
                      className="text-emerald-600 mt-0.5 flex-shrink-0"
                    />
                    <div>
                      <p
                        className="text-sm font-semibold text-[var(--charcoal)]"
                        data-testid="interest-contacted-label"
                      >
                        Contacted {formatRelative(interest.last_contacted_at)}
                      </p>
                      <p className="text-xs text-[var(--text-muted)]">
                        {new Date(interest.last_contacted_at).toLocaleString()}
                        {interest.last_contacted_by
                          ? ` · by ${interest.last_contacted_by}`
                          : ""}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => onMarkContacted(interest.id)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white text-[var(--charcoal)] border border-[var(--light-border)] text-xs font-semibold hover:bg-white/80"
                    data-testid="mark-contacted-again-btn"
                  >
                    <PhoneCall size={12} />
                    Log another contact
                  </button>
                </div>
              ) : (
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div className="flex items-start gap-2.5">
                    <span className="w-9 h-9 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0">
                      <PhoneCall size={16} />
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-[var(--charcoal)]">
                        Not contacted yet
                      </p>
                      <p className="text-xs text-[var(--text-muted)]">
                        Click after you've reached out so this lead leaves the
                        "needs attention" list.
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => onMarkContacted(interest.id)}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[var(--lime)] text-[var(--dark-bg)] text-sm font-bold hover:bg-[var(--lime-dark)] transition-colors shadow-sm"
                    data-testid="mark-contacted-btn"
                  >
                    <CheckCircle2 size={14} />
                    Mark as contacted
                  </button>
                </div>
              )}
            </div>
            <h4 className="text-xs uppercase tracking-wider font-semibold text-[var(--text-muted)] mb-2">
              Pipeline status
            </h4>
            <div className="flex flex-wrap gap-2">
              {STATUS_OPTIONS.map((s) => {
                const active = interest.status === s.value;
                return (
                  <button
                    key={s.value}
                    type="button"
                    onClick={() => onUpdateStatus(interest.id, s.value)}
                    data-testid={`set-status-${s.value}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                      active
                        ? `${s.className} border-current`
                        : "bg-white text-[var(--charcoal)] border-[var(--light-border)] hover:border-[var(--charcoal)]"
                    }`}
                  >
                    {s.label}
                  </button>
                );
              })}
            </div>
          </div>

          {interest.notes && (
            <Info label="Their note to us">
              <span className="whitespace-pre-line">{interest.notes}</span>
            </Info>
          )}

          <div>
            <label className="block text-sm font-medium text-[var(--charcoal)] mb-1">
              Admin notes
            </label>
            <textarea
              data-testid="interest-admin-notes"
              className={`w-full h-24 px-3 py-2 rounded-lg border text-[var(--charcoal)] resize-none ${
                notesError
                  ? "border-red-400 ring-1 ring-red-300"
                  : "border-[var(--sage)]/20"
              }`}
              value={notes}
              onChange={(e) => {
                setNotes(e.target.value);
                if (notesError) setNotesError(false);
              }}
              placeholder="Internal notes (not shared)"
            />
            {notesError && (
              <p className="text-xs text-red-600 mt-1" data-testid="notes-required-error">
                A reason is required in the notes before marking as a scammer.
              </p>
            )}
          </div>
        </div>

        <div className="p-6 border-t border-[var(--sage)]/10 flex flex-col-reverse sm:flex-row sm:justify-between gap-3">
          <div className="flex items-center gap-4">
            <button
              onClick={() => onDelete(interest.id)}
              className="text-sm text-red-600 hover:underline inline-flex items-center gap-1"
              data-testid="delete-interest-btn"
            >
              <Trash2 size={14} />
              Delete record
            </button>
            {!interest.is_scammer && (
              <button
                onClick={handleMarkScammer}
                className="text-sm font-semibold text-red-600 hover:underline inline-flex items-center gap-1"
                data-testid="mark-scammer-btn"
              >
                <ShieldAlert size={14} />
                Mark as scammer
              </button>
            )}
          </div>
          <div className="flex gap-2 sm:justify-end">
            <button onClick={onClose} className="btn-secondary">Close</button>
            <button
              onClick={() => onUpdateNotes(interest.id, notes)}
              disabled={!dirty}
              className="btn-primary disabled:opacity-50"
              data-testid="save-interest-notes"
            >
              Save notes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Info({ label, children }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-[var(--olive)]">
        {label}
      </div>
      <div className="text-[var(--charcoal)]">{children || "-"}</div>
    </div>
  );
}
